import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { setReservationData } from "../features/commerce/commerceSlice";
import { setIsConfirmationPageAccessible, setShowContractPriceForReservation } from '../features/globalStates/globalStatesSlice';
import { Box, Typography } from "@mui/material";
import { Button } from '../components';
import { Spinner } from "../components/Spinner";
import ReservationDetailsCard from '../components/Reservation/ReservationDetailsCard';
import { AlertMessage } from "../components/AlertMessage";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useOktaAuth } from '@okta/okta-react';
import { formattedPrice, sortAddresses, sortProductsByPriority } from "../utils/Helper";
import { mapProductNames } from "../utils/HelperFunctions/OrderAndReservationHelper";
import { mapProductNamesFormatted } from "../utils/HelperFunctions/OrderAndReservationHelper";

import { createReservationApi, getReservationById, updateReservationApi, cancelReservation } from '../utils/Reservation/ReservationApi';
import AddressSelectionModal from "../components/Reservation/AddressSelectionModal";
import { UserAddressFormModal } from "../components/AddressesForm/UserAddressForm";
import ConfirmationModal from '../components/Reservation/ConfirmationModal';
import { getPricingApi, getPricingWithCustomerIdApi } from '../utils/PricingService/PricingApi';
import { getUserByEmail, updateUserPreferencesApi } from '../utils/UserService/UserApis';
import {CONTRACT_TYPES, NO_CONTRACT, UNAVAILABLE, VACCINE_RESERVATION_HEADER} from '../constants';
import { getRoundedDoseMessage } from '../utils/HelperFunctions/OrderAndReservationHelper';
import { getReservableProductsApi } from "../utils/CatalogService/CatalogAPI";
import { createPricingRequestPayload, createPricingV2RequestPayload } from "./Ordering/OrderHelper";
import MessageBanner from "../components/MessageBanner/MessageBanner";
import { useFeatureFlag } from '../context/FeatureFlagContext';
import ReservationGPOComponent from "@/components/Reservation/ReservationGPOComponent";


const ReservationPage = () => {
    const isReservationContractEnabled = useFeatureFlag("IS_OVERLAPPING_CONTRACT_ENABLED");
    const isReservationNextGenBannerEnabled = useFeatureFlag("IS_RESERVATIONS_NEXTGEN_BANNER_ENABLED");
    const dispatch = useDispatch();
    const history = useHistory();
    const locale = useSelector((state) => state.globalStates.locale);
    const isLoggingIn = useSelector((state) => state.globalStates.isLoggingIn);
    const userData = useSelector((store) => store?.commerce?.userData);
    const emailAddress = useSelector((state) => state.globalStates.emailAddress);
    const pricingData = useSelector((store) => store?.commerce?.pricingData);
    const defaultAddress = userData?.addresses?.find(address => address.addressType === 'ship_to');
    const defaultBillingAddress = userData?.addresses?.find(address => address.addressType === 'bill_to');
    const defaultSoldToAddress = userData?.addresses?.find(address => address.addressType === 'sold_to');
    const { authState } = useOktaAuth() || '';
    const isAuthenticated = authState?.isAuthenticated;
    const [processing, setProcessing] = useState(false);
    const [isUserDataProcessing, setIsUserDataProcessing] = useState(false);
    const [isReservationProcessing, setIsReservationProcessing] = useState(false);
    const [isProductsProcessing, setIsProductsProcessing] = useState(false);
    const [isPricingProcessing, setIsPricingProcessing] = useState(false);
    const isPageLoading = isUserDataProcessing || isReservationProcessing || isProductsProcessing || isPricingProcessing;
    const userId = userData?.authenticatedId
    const internalUserId = userData?.userId;

    const { reservationProducts } = useSelector((store) => store?.commerce) || [];
    const { reservation } = useSelector((store) => store?.commerce);
    const { reservationData } = useSelector((store) => store?.commerce);

    const [deliverySchedule, setDeliverySchedule] = useState('');

    const alertRef = useRef(null);
    const { globalErrorMessage = [] } = useSelector(store => store?.globalMessages);
    const generalApiErrorData = globalErrorMessage && globalErrorMessage.length && globalErrorMessage.filter((data) => data?.fields?.code === "GENERAL_API_ERROR");
    const generalApiError = generalApiErrorData && generalApiErrorData[0]?.fields?.message;
    const [alertMsg, setAlertMsg] = useState({ type:'info', text:'' });
    const [openAddressModal, setOpenAddressModal] = useState(false);
    const [openConfirmationModal, setOpenConfirmationModal] = useState(false)
    const [confirmationModalContentType, setConfirmationModalContentType] = useState('')
    const [totalDosesQty, setTotalDoses] = useState(0);
    const [showUserAddressFormModal, setShowUserAddressFormModal] = useState(false);
    const disableContinue = totalDosesQty === 0 || !defaultBillingAddress?.id;

    const { reservationId } = useParams();
    const isEditing = !!reservationId;
    const [refreshReservationData, setRefreshReservationData] = useState(false);
    const [selectedContract, setSelectedContract] = useState(reservation?.reservation?.contractInfo);
    const showContractPriceForReservation = useSelector((state) => state.globalStates.showContractPriceForReservation);
    const isDiscountEnabled = useFeatureFlag("ONLINE_DISCOUNT");

    const handleErrorMsg = (errorMsg) => {
        setProcessing(false);
        setAlertMsg({ type: 'error', text: errorMsg });
        alertRef.current?.openAlert('');
    }

    const getMedicalIdentifier = () => {
        let medicalIdentifier = null;
        medicalIdentifier = selectedContract?.medicalIdentifier;

        if (!medicalIdentifier) {
            medicalIdentifier = userData?.licenses?.find(license => license.issuer === "HIN" || license.issuer === "DEA")?.number;
        }
        return medicalIdentifier || 'anonymous';
    }

    const getShipToOrgId = (addressId) => {
        const matched = shipToAddresses?.find(address => String(address.id) === String(addressId));
        return matched?.organizationId != null ? String(matched.organizationId) : null;
    }

    // Retrieve reservation data from APIs
    useEffect(() => {
        const createReservations = async () => {
            try {
                setIsReservationProcessing(true);
                setIsProductsProcessing(true);
                setIsPricingProcessing(true);
                if (isAuthenticated && userId) {
                    if (isEditing) {
                        await dispatch(getReservationById({ userId, reservationId })).unwrap();
                    }
                    else {
                        await dispatch(createReservationApi({ userId, userEmail: emailAddress })).unwrap();
                    }
                }
                const res = await dispatch(getReservableProductsApi({locale: 'US'})).unwrap();
                const reservationProducts=res?.data?.productList
                try {
                    !isReservationContractEnabled && await dispatch(getPricingApi({payload: createPricingRequestPayload(reservationProducts, "RESERVATION", isDiscountEnabled)})).unwrap()
                } catch (error) {
                    const apiErrorMessage = error?.message || generalApiError;
                    setAlertMsg({ type: 'error', text: apiErrorMessage });
                    alertRef.current?.openAlert('');
                }
            } catch (error) {
                setAlertMsg({ type: 'error', text: error?.message || generalApiError })
                alertRef.current?.openAlert('');
            } finally {
                setIsReservationProcessing(false);
                setIsProductsProcessing(false);
                setIsPricingProcessing(false);
            }
        };
    
        createReservations();
    }, [dispatch, isAuthenticated, userId]);

    useEffect(() => {
        if (isReservationContractEnabled) {
            const fetchPricing = async () => {
                const customerId = selectedContract?.medicalIdentifier || 'anonymous';
                await safeDispatch(
                    getPricingWithCustomerIdApi({
                        payload: createPricingV2RequestPayload(reservationProducts, internalUserId),
                        customerId: customerId
                    })
                );
            };

            reservationProducts?.length && fetchPricing();
        }
    }, [selectedContract, dispatch, reservationProducts]);


    /** Get all ship to addresses and find default */
    const shipToAddresses = useMemo(() => {
        const addresses = userData?.addresses || [];
        return addresses
            .filter(address => address.addressType === "ship_to")
            .map(address => ({
                ...address,
                defaultAddress: address.id === defaultAddress?.id,
            }))
            .sort(sortAddresses);
    }, [defaultAddress]);


    useEffect(() => {
        // if there is reservation data, map through reservation Entries,
        // else map through products to create default product info
        if (reservation?.reservationEntries?.length) {
            let reservationEntries = reservation?.reservationEntries?.map(reservationEntry => {
                let shipToOrgId = getShipToOrgId(reservationEntry.deliveryAddressId);
                return {
                    reservationEntryId: reservationEntry.reservationEntryId,
                    deliveryAddressId: reservationEntry.deliveryAddressId,
                    productId: reservationEntry.productId,
                    quantity: reservationEntry.quantity,
                    purchaseOrderNumber: reservationEntry.purchaseOrderNumber,
                    shipToOrganizationId: shipToOrgId
                }
            });
            dispatch(setReservationData({...reservation, reservationEntries}));
        } else {
            let reservationEntries = reservationProducts?.length && reservationProducts?.map(product => {
                const productId = product?.materialMasterId;
                let addressId = reservation?.length && reservation?.reservationEntries[0]?.deliveryAddressId;

                let shipToOrgId = getShipToOrgId(addressId);
                if (addressId === undefined || (addressId === null && shipToAddresses.length)) {
                    addressId = shipToAddresses[0]?.id;
                    shipToOrgId= String(shipToAddresses[0]?.organizationId);
                }
                const uniqueSuffix = Math.random().toString(36).substr(2, 9);
                const reservationEntryId = `${productId}-${uniqueSuffix}`;
                return {
                    reservationEntryId: reservationEntryId,
                    reservationId: reservation?.reservation?.reservationId,
                    deliveryAddressId: addressId,
                    productId,
                    quantity: 0,
                    purchaseOrderNumber: "",
                    shipToOrganizationId: shipToOrgId
                }
            });
            dispatch(setReservationData({...reservation, reservationEntries: reservationEntries}));
        }
        setDeliverySchedule(reservation?.reservation?.deliverySchedule || "INITIAL")
        setRefreshReservationData(false);
    }, [reservationProducts, refreshReservationData, reservation, dispatch, shipToAddresses])


    const getContractPrice = () => {
        return selectedContract 
        && pricingData?.pricesByPriceReference?.find(price => price?.priceReferenceId === selectedContract?.id);
    }
        
    const getPrices = () => {
        return getContractPrice() || pricingData?.pricesByPriceReference?.find(
            price => price?.priceReferenceType === CONTRACT_TYPES.CUSTOMER_PRICE_LIST);
    };

    const handleContractChange = async (selectedPriceContract) => {
        const { id: priceContractId, displayName, medicalIdentifier, contractType } = selectedPriceContract;
        const updateUserPreferencesRequestBody = {
            userId: userData?.userId,
            priceContract: {
                id: priceContractId,
                displayName: displayName,
                medicalIdentifier: medicalIdentifier,
                priceReferenceType: contractType,
            },
        };
        try {
            setProcessing(true);
            const response = await updateUserPreferencesApi(updateUserPreferencesRequestBody);
            if (response.status === 200) {
                dispatch(setShowContractPriceForReservation(true));
                dispatch(getUserByEmail({ emailAddress: response?.data?.email }));
                
                dispatch(getPricingApi({payload: createPricingRequestPayload(reservationProducts, "RESERVATION", isDiscountEnabled)}));

            }
        } catch (error) {
            setAlertMsg({ type: "error", text: error.message || generalApiError });
            alertRef.current?.openAlert("");
        } finally {
            setProcessing(false);
        }
    };

    const safeDispatch = async (action) =>{
        try{
            return await dispatch(action).unwrap();
        } catch(error) {
            const apiErrorMessage = error?.message || generalApiError;
            setAlertMsg({ type: 'error', text: apiErrorMessage });
            alertRef.current?.openAlert('');
        }
    }
    const handleReservationContractChange = async (selectedContract) => {
        const customerId = selectedContract?.medicalIdentifier || 'anonymous';
        await safeDispatch(
            getPricingWithCustomerIdApi({
                payload: createPricingV2RequestPayload(reservationProducts, internalUserId),
                customerId: customerId
            })
        );
        selectedContract?.medicalIdentifier && dispatch(setShowContractPriceForReservation(true));
    };
    
    useEffect(() => {
        if (selectedContract && isReservationContractEnabled) {
            (selectedContract?.id === "no-contract" || selectedContract?.contractId === "no-contract" || selectedContract?.contractId === null) ?
            dispatch(setShowContractPriceForReservation(false))
            :
            dispatch(setShowContractPriceForReservation(true));
        } else {
            dispatch(setShowContractPriceForReservation(false));
        }
    }, [selectedContract, dispatch]);

    const allPrices =  getPrices();
    const priceReferenceId = allPrices?.priceReferenceId;
    const priceDisplayName = allPrices?.displayName;
    let productPricingMap = allPrices?.productPrices.reduce((all, productPrice) => {
        return { 
            ...all, 
            [productPrice?.productId]: {
                listPrice: productPrice?.listPriceFormatted,
                contractPrice: productPrice?.finalPriceFormatted
            } 
        }
    }, {}) || {};

    const productMap = mapProductNames(reservationProducts);
    const productMapFormatted = mapProductNamesFormatted(reservationProducts);

    const isContractAvailable = (contract) => {
        const contractId = contract?.id ?? contract?.contractId;
        return !!contractId && contractId !== NO_CONTRACT;
    };


    /** Array of API reservation entries to send to confirm API or update reservation API */
    const reservationEntriesModel = reservationData?.reservationEntries || [];

    // Helper function to create a display entry for a reservation details card
    const createDisplayEntry = (reservationEntry) => {
        if (!reservationEntry) return null;
        const { productId, deliveryAddressId, purchaseOrderNumber } = reservationEntry;
        const pricing = productPricingMap[productId] || {};
        const address = shipToAddresses?.find(({ id }) => String(id) === String(deliveryAddressId));
        const productName = productMap[productId] || "Unknown Product";
        const productNameFormatted = productMapFormatted[productId] || "Unknown Product";
        const shipToOrganizationId = address?.organizationId;

        return {
          id: reservationEntry.reservationEntryId,
          doses: parseInt(reservationEntry?.quantity, 10) || 0,
          productId,
          productName,
          productNameFormatted,
          listPriceValue: pricing?.listPrice,
          listPrice: formattedPrice(pricing?.listPrice),
          contractPriceValue: pricing?.contractPrice,
            contractPrice: showContractPriceForReservation && isContractAvailable(selectedContract)
                ? formattedPrice(pricing?.contractPrice)
                : UNAVAILABLE,
            address,
          purchaseOrderNumber,
          shipToOrganizationId
        };
      };
    // Map reservation entries to display entries and filter out undefined entries
    const reservationEntriesDisplay = reservationEntriesModel
        .map(createDisplayEntry)
        .filter(entry => entry !== null);

    // Helper function to determine if an addressId is unique within the array
    const isUniqueAddressId = (addressId, index, self) => {
        return self.findIndex(entry => entry.deliveryAddressId === addressId) === index;
      };
    // Get all unique address IDs
    const reservationAddressIds = reservationEntriesModel
        .filter(({ deliveryAddressId }, index, self) => deliveryAddressId && isUniqueAddressId(deliveryAddressId, index, self))
        .map(({ deliveryAddressId }) => deliveryAddressId);
    
    const handleDosesChange = (reservationEntryId, doses) => {
        // Find the index of the reservation entry by reservationEntryId
        const reservationEntryIndex = reservationEntriesModel.findIndex(entry => entry.reservationEntryId === reservationEntryId);
        
        // If the entry is not found, exit the function
        if (reservationEntryIndex === -1) return;
        
        // Retrieve the entry to be updated
        const entryToUpdate = reservationEntriesModel[reservationEntryIndex];
        // Get the shipToOrganizationId from the deliveryAddressId
        const shipToOrgId = getShipToOrgId(entryToUpdate?.deliveryAddressId);

        // If it's the entry we want to update, return a new object with the updated doses
        const roundOffQty = (Math.ceil(parseInt(doses) / 10)) * 10;
        if (roundOffQty !== 0 && doses !== roundOffQty) {
            setAlertMsg({type: 'info', text: getRoundedDoseMessage(roundOffQty)})
            alertRef.current?.openAlert('');

        }
    
        // Create a new entry with the updated doses
        const updatedEntry = {
            ...entryToUpdate,
            quantity: roundOffQty,
            shipToOrganizationId: shipToOrgId
        };

        // Create a new array with the updated entry
        const updatedReservationEntries = [
            ...reservationEntriesModel.slice(0, reservationEntryIndex),
            updatedEntry,
            ...reservationEntriesModel.slice(reservationEntryIndex + 1)
        ];

        const { totalDoses } = reservationPricingInfo(updatedReservationEntries)
        setTotalDoses(totalDoses);

        // Dispatch the new state
        dispatch(setReservationData({
            ...reservationData,
            reservationEntries: updatedReservationEntries
        }));
    };

    const handleDeleteAddressRow =  (reservationAddressId) => {
        const updatedReservationEntries = reservationData?.reservationEntries?.filter((entry) => {
            return entry.deliveryAddressId !== reservationAddressId

        });
        const { totalDoses } = reservationPricingInfo(updatedReservationEntries)
        setTotalDoses(totalDoses);
        dispatch(setReservationData({ reservationEntries: updatedReservationEntries }));
    }

    const MODALS = { ADDRESS_SELECTION: 'addressSelection',
        ADDRESS_FORM: 'addressForm',
        USER_ADDRESS_FORM: 'userAddressForm',
        CONFIRMATION: 'confirmation' }

    const closeModal = (modalName) => {
        modalName === MODALS.ADDRESS_SELECTION && setOpenAddressModal(false);
        modalName === MODALS.USER_ADDRESS_FORM && setShowUserAddressFormModal(false);
        modalName === MODALS.CONFIRMATION && setOpenConfirmationModal(false);
    }
    const handleAddNewAddress = () => {
        closeModal(MODALS.ADDRESS_SELECTION);
        setShowUserAddressFormModal(true);
    }

    const handleDiscardChange = () => {
        dispatch(setReservationData({ reservation: {}, reservationEntries: [] }))
        setRefreshReservationData(true)
        setOpenConfirmationModal(false)
    }

    const handleCancelReservation = () => {
        dispatch(cancelReservation({ userId, reservationId: reservation?.reservation?.reservationId })).unwrap()
            .then(() => {
                const reservationNumber = reservationData?.reservation?.reservationNumber;
                dispatch(setIsConfirmationPageAccessible(true));
                history.push(`/${locale}/confirmation/${reservationNumber}`);
            })
            .catch((error) => {
                setOpenConfirmationModal(false);
                setAlertMsg({ type: 'error', text: generalApiError })
                alertRef.current?.openAlert('');
            })
    }

    const handlePoNumberChange = async (reservationEntriesToUpdate, purchaseOrderNumber) => {

          const updatedReservationEntries = reservationEntriesModel?.map(existingEntry => {
              const matchingReservationEntry = reservationEntriesToUpdate.find(entryToUpdate => entryToUpdate?.id === existingEntry?.reservationEntryId);
              if (matchingReservationEntry) {
                  return { ...existingEntry, purchaseOrderNumber };
              }
              return existingEntry;
          });
          await dispatch(setReservationData({ reservationEntries: updatedReservationEntries }));
      };

    /** Reservation card components. */
    const getReservationDetailCards = reservationAddressIds.map((reservationAddressId, i) => {
        const reservationEntriesForThisAddress = reservationEntriesDisplay?.filter(({address}) => String(address?.id) === String(reservationAddressId));
        sortProductsByPriority(reservationEntriesForThisAddress, 'productId');
        if (isPageLoading) {
            return <Spinner processing={true} key={reservationAddressId}/>
        }
        return <ReservationDetailsCard
                    key={reservationAddressId}
                    entries={reservationEntriesForThisAddress} 
                    handleDosesChange={handleDosesChange}
                    deleteAddressRow={() => handleDeleteAddressRow(reservationAddressId)}
                    updateDosesQty={(count) => setTotalDoses(count)}
                    setExternalProcessing={setProcessing}
                    errorMsgHandler={handleErrorMsg}
                    handlePoNumberChange={handlePoNumberChange}
                />
    })

    /** Building out products to be displayed for unauthenticated users*/
    const unAuthedProducts = reservationProducts?.length &&  reservationProducts?.map((product, id) => {
        const productId = product?.materialMasterId;

        return {
            id,
            doses: 0,
            productId: productId,
            productName: productMap[productId],
        }
    }) || []

    /** Unauthenticated reservation details card component */
    const getUnAuthenticatedReservationDetailCards = 
        <ReservationDetailsCard entries={unAuthedProducts}/>

    const reservationPricingInfo = (data) => {
        return data.reduce(({totalDoses, totalPrice}, product) => {
            const pricePerDose = (product.contractPriceValue !== undefined) ? product.contractPriceValue : product.listPriceValue;
            return {
                totalDoses: totalDoses + product.doses,
                totalPrice: totalPrice + product.doses * pricePerDose
            };
        }, {totalDoses: 0, totalPrice: 0})
    }

    const handleContinue = async (isEditing = false) => {
        setProcessing(true);
        const {totalDoses, totalPrice} = reservationPricingInfo(reservationEntriesDisplay)

        // additional details needed for update API
        const reservationDetails = {
            userId,
            medicalIdentifier: getMedicalIdentifier(),
            reservation: {
                ...reservationData.reservation,
                priceReferenceIdentifier: priceReferenceId,
                deliverySchedule: deliverySchedule,
                billingAddressInformation: {
                    addressId: defaultBillingAddress?.id,
                    organizationId: defaultBillingAddress?.organizationId
                },
                reservationPricingInfo: {
                    totalPrice,
                    totalDoses,
                },
                internalUserId: internalUserId,
                displayName: priceDisplayName,
                soldToOrganizationId: defaultSoldToAddress?.organizationId,
                ...(isReservationContractEnabled && {
                    contractInfo: {
                        contractId: selectedContract?.id === NO_CONTRACT ? NO_CONTRACT : selectedContract?.id,
                        contractDisplayName: selectedContract?.displayName,
                    }
                })
            }
        };

        try {
            dispatch(setReservationData(reservationDetails));
            await dispatch(updateReservationApi({
                payload: {
                    ...reservationDetails,
                    reservationEntries: reservationData.reservationEntries
                }
            }));
            if (isEditing) history.push(`/${locale}/summary/${reservationId}`);
            else history.push(`/${locale}/summary`);
            setProcessing(false);
        } catch (error) {
            setProcessing(false);
            setAlertMsg({ type: 'error', text: error?.message || generalApiError })
            alertRef.current?.openAlert('')
        } 
    }

    const handleCancelOrDiscard = (eventType) => {
        setConfirmationModalContentType(eventType)
        setOpenConfirmationModal(true)
    }

    const getAllSelectedAddress = () => {
        return reservationAddressIds.map((reservationAddressId, i) => {
            return reservationEntriesDisplay?.filter(({address}) => String(address?.id) === String(reservationAddressId))[0]?.address
        });
    }

    const getReservationProductCodes = (reservationProducts?.length && reservationProducts?.map(product =>
        product?.materialMasterId
    )) || [];

    const includeNextGenReservation = () => {
        const createdDateStr = reservation?.reservation?.createdDate;
        if (!createdDateStr) return false;

        const createdDate = new Date(createdDateStr);
        //estimate launch date for NextGen
        const cutoffDate = new Date('2025-06-08');

        return createdDate > cutoffDate;
    };

    return (
        !isLoggingIn &&
        <>
            {<AlertMessage data-testid='errorAlert' variant={"filled"} type={alertMsg.type} message={alertMsg.text} sx={{ top: 120}} ref={alertRef} />}
            <Spinner processing={processing || isUserDataProcessing} />
            {openAddressModal && <AddressSelectionModal openModal={openAddressModal} closeModal={() => closeModal(MODALS.ADDRESS_SELECTION)} allSelectedAddresses={getAllSelectedAddress()} 
                handleAddNewAddress={handleAddNewAddress} isOrdering={false}
            />
            }
            {showUserAddressFormModal &&
              <UserAddressFormModal
                openModal={showUserAddressFormModal}
                closeModal={() => closeModal(MODALS.USER_ADDRESS_FORM)}
                addressType={"ship_to"}
                setExternalProcessing={setProcessing}
                isAddingAdditionalLocation={true}
                errorMsgHandler={handleErrorMsg}
              />
            }
            {openConfirmationModal &&
                <ConfirmationModal
                    openModal={openConfirmationModal}
                    closeModal={() => closeModal(MODALS.CONFIRMATION)}
                    contentType={confirmationModalContentType}
                    handleConfirm={confirmationModalContentType === 'cancel' ? handleCancelReservation : handleDiscardChange}
                />
            }
            <Box display="flex" flexDirection="column" m={4}>
                <Typography variant="h1" style={{ marginBottom: '16px'}}>{VACCINE_RESERVATION_HEADER}</Typography>
                {
                    isAuthenticated && isReservationContractEnabled ? 
                        <ReservationGPOComponent
                            productsForPricing={getReservationProductCodes}
                            onContractChange={isReservationContractEnabled? handleReservationContractChange : handleContractChange}
                            selectedContract={selectedContract}
                            setSelectedContract={setSelectedContract}
                            isEditing={isEditing}
                        />
                    :
                        <MessageBanner
                            type="info"
                            headerText= "Contracts are not available at this time"
                            bannerContentComponent={
                                <Typography>
                                    You can continue to reserve your doses. Check back again to see contract pricing. You will be able to view and edit your reservation until ordering opens. Reservations are subject to FDA approval or emergency use authorization of 2025-2026 formula.
                                </Typography>
                            }
                            dataTestId="reservationContractSelectionDisabledBanner"
                        />
                }
                {
                    isEditing && isReservationNextGenBannerEnabled && !includeNextGenReservation() &&
                    <Box mt={3}>
                        <MessageBanner
                            type="warning"
                            headerText= "Looking for mNEXSPIKE?"
                            bannerContentComponent={
                            <Box>
                                <Typography>
                                    Follow these steps to cancel this reservation and place a new reservation with mNEXSPIKE® (COVID-19 Vaccine, mRNA) 2025-2026 Formula:
                                </Typography>
                                <Typography>
                                    1. Cancel this reservation using the cancel button in the bottom left.
                                </Typography>
                                <Typography>
                                    2. Return to the Homepage and click on “Reserve” to place a new reservation that includes mNEXSPIKE.
                                </Typography>
                                </Box>
                            }
                            dataTestId="reservationContractSelectionDisabledBanner"
                        />
                    </Box>
                }
                { isAuthenticated ?
                    getReservationDetailCards : getUnAuthenticatedReservationDetailCards 
                }
                <Box flex={1} minWidth={'300px'} mb={3} mt={5}>
                    <Button
                        buttonType={'mds-secondary'}
                        data-testid='deliverToAddressBtn'
                        muiIconLeft={AddCircleOutlineIcon}
                        disabled={!isAuthenticated}
                        onClick={() => {setOpenAddressModal(true)}}
                    >
                        Reserve for additional address
                    </Button>
                </Box>
                {
                    isAuthenticated && !isEditing &&
                    <Box display="flex" justifyContent="center" mt={4}>
                        <Button
                            data-testid='reservationContinueBtn'
                            buttonType='mds-primary'
                            sx={{textTransform: 'none'}}
                            muiIconRight={ArrowForwardIcon}
                            disabled={disableContinue}
                            onClick={() => handleContinue()}
                        >
                            {isReservationContractEnabled? 'Save and continue' : 'CONTINUE'}
                        </Button>
                    </Box>
                }
                {
                    isEditing &&
                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={4}>
                        <Button buttonType={'secondary'} onClick={() => {handleCancelOrDiscard('cancel')}}>
                            Cancel Reservation
                        </Button>
                        <Box display="flex" alignItems="center">
                            <Box mr={3}>
                                <Button
                                    buttonType={'mds-secondary'}
                                    data-testid='discardChangeBtn'
                                    onClick={() => {handleCancelOrDiscard('discard')}}
                                >
                                    Discard Changes
                                </Button>
                            </Box>
                            <Button
                                data-testid='saveEditChangeBtn'
                                buttonType={'mds-primary'}
                                onClick={() => handleContinue(isEditing)}
                            >
                                Save Changes
                            </Button>
                        </Box>
                    </Box>
                }
            </Box>
        </>
    )
}

export default ReservationPage
